import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useGrowthBook } from '@growthbook/growthbook-react';
import MuiThemeProvider from '@mui/material/styles/ThemeProvider';
import MuiTypography from '@mui/material/Typography';
import Trans from 'next-translate/Trans';
import useTranslation from 'next-translate/useTranslation';

import { useAppDispatch, useAppSelector, useDialog, useOnboardingTask, useTracking, useTraits } from '@/hooks';
import { selectCompanyId } from '@/redux/features/authSlice';
import { setTrackingMode, setTrackingModeStep } from '@/redux/features/onboardingSlice';

import CompanyRequests from '../../../requests/company.requests';
import { CompanyTrackingMode, StepCommonProps, StepInfo, TrackingModeStep } from '../../../types';

import TrackingModeStepVariantB from './TrackingModeStepVariantB';
import TrackingSilentInteractiveContent, { theme } from './TrackingSilentInteractiveContent';

type TrackingCardKey = 'visible' | 'silent' | null;

const TrackingModeStepComponent: FC<StepCommonProps> = ({ flow }) => {
  const { t } = useTranslation();
  const { company } = useAppSelector(state => state.auth);
  const onboardingStep = useAppSelector(state => state.onboarding);

  const growthBook = useGrowthBook();
  const dispatch = useAppDispatch();
  const { trackOnboardingStep, trackAppEvent, trackStepChangePage, trackStepChangeEvent } = useTracking(flow);
  const { goToNextStep, getNextStep, goToPreviousStep, getPreviousStep } = useOnboardingTask(flow);
  const { updateTraits } = useTraits();
  const companyId = useSelector(selectCompanyId);
  const useFriendlyText = growthBook?.getFeatureValue<boolean>('silent-to-automatic', false);

  const hasUpdatedCompanySettings = useRef(false);
  const hasTrackedPageView = useRef(false);
  const hasTrackedTrackingMode = useRef(false);
  const hasTrackedPreviousStep = useRef(false);
  const hasSelectedDefaultCard = useRef(false);

  const stepData = useMemo<Array<{ key: 'visible' | 'silent'; title: string; desc: string }>>(() => [
    {
      key: 'visible',
      title: 'onboarding:tracking_mode_step.interactive_card.title',
      desc: 'onboarding:tracking_mode_step.interactive_card.desc',
    },
    {
      key: 'silent',
      title: useFriendlyText
        ? 'onboarding:tracking_mode_step.silent_card.title_automatic'
        : 'onboarding:tracking_mode_step.silent_card.title',
      desc: 'onboarding:tracking_mode_step.silent_card.desc',
    },
  ], [useFriendlyText]);

  const getCardKeyFromTrackingMode = (mode?: CompanyTrackingMode): TrackingCardKey => {
    if (mode === 'interactive') return 'visible';
    if (mode === 'silent') return 'silent';
    return null;
  };

  const backendTrackingModeStep = (() => {
    const step = company?.companySettings?.custom?.trackingModeStep;
    if (step === 'visible' || step === 'silent') return step;
    if (step === 'interactive') return 'visible';
    return null;
  })();

  const storedTrackingModeStep = getCardKeyFromTrackingMode(onboardingStep.trackingModeStep);
  const [selectedCardKey, setSelectedCardKey] = useState<TrackingCardKey>(
    storedTrackingModeStep || backendTrackingModeStep || null,
  );

  const updateCompanySettings = useCallback(async (companyId: string) => {
    await CompanyRequests.putCompanySettings(companyId, {
      custom: {
        onboardingStep: 'trackingModeStep',
      },
    });
  }, []);

  useEffect(() => {
    if (!hasTrackedPageView.current) {
      hasTrackedPageView.current = true;
      const stepName = 'Tracking Type Screen';
      trackStepChangePage(stepName);
      trackStepChangeEvent(flow.trackingMode ?? null, { stepName });

      if (!hasUpdatedCompanySettings.current) {
        hasUpdatedCompanySettings.current = true;
        updateCompanySettings(companyId);
      }
    }
  }, [flow, companyId, updateCompanySettings, trackStepChangeEvent, trackStepChangePage]);

  useEffect(() => {
    if (hasSelectedDefaultCard.current) return;

    const defaultValue = storedTrackingModeStep || backendTrackingModeStep;

    if (defaultValue) {
      hasSelectedDefaultCard.current = true;
      setSelectedCardKey(defaultValue);
      handleTrackingModeSelection(defaultValue);
    } else {
      hasSelectedDefaultCard.current = true;
      handleTrackingModeSelection('visible');
    }
  }, [storedTrackingModeStep, backendTrackingModeStep]);

  const trackSelectedTrackingMode = () => {
    if (hasTrackedTrackingMode.current || !selectedCardKey) return;

    const trackingMode: CompanyTrackingMode = selectedCardKey === 'visible' ? 'interactive' : 'silent';

    trackOnboardingStep('Tracking Type Selected Option', {
      trackingtype: trackingMode,
      ownership: 'company',
    });

    hasTrackedTrackingMode.current = true;
  };

  const handleTrackingModeSelection = async (cardKey: 'visible' | 'silent') => {
    if (cardKey === selectedCardKey) return;

    const trackingMode: CompanyTrackingMode = cardKey === 'visible' ? 'interactive' : 'silent';
    const tasksMode = trackingMode === 'interactive' ? 'preset' : 'off';
    flow.trackingMode = trackingMode;

    setSelectedCardKey(cardKey);

    dispatch(setTrackingModeStep(trackingMode));
    dispatch(setTrackingMode(trackingMode));

    growthBook?.setAttributes({ ...growthBook?.getAttributes(), tracking_mode: trackingMode });

    updateTraits({
      companyTraits: {
        onboarding_step: getNextStep()?.stepName,
        taskMode: tasksMode,
      },
    });

    await CompanyRequests.putCompanySettings(companyId, {
      trackingMode,
      tasksMode,
      custom: {
        trackingModeStep: cardKey,
      },
    });
  };

  const handleNextButtonClick = async () => {
    if (!selectedCardKey) return;
    trackSelectedTrackingMode();

    goToNextStep();
  };

  const handleGoToPreviousStep = () => {
    if (!hasTrackedPreviousStep.current) {
      const previousStep = getPreviousStep();
      if (previousStep) {
        hasTrackedPreviousStep.current = true;
        trackStepChangePage(previousStep.stepName);
        trackStepChangeEvent(flow.trackingMode ?? null, { stepName: previousStep.stepName });
      }
    }
    goToPreviousStep();
  };

  const handleLearnMoreClick = () => {
    trackAppEvent('Clicked Learn More Onboarding', { category: 'Onboarding' });
  };

  return (
    <TrackingModeStepVariantB
      flow={ flow }
      selectedCardKey={ selectedCardKey }
      stepData={ stepData }
      handleTrackingModeSelection={ handleTrackingModeSelection }
      handleNextButtonClick={ handleNextButtonClick }
      goToPreviousStep={ handleGoToPreviousStep }
      InfoComponent={
        <p style={{
          color: '#574841',
          fontWeight: 400,
          fontSize: '14px !important',
          lineHeight: '20px',
          letterSpacing: '0.25px',
          marginBottom: '0',
        }}>
          { t('onboarding:tracking_mode_step.variant_b.info.text') }
          <a
            href="https://support.timedoctor.com/knowledge/automatic-vs.-interactive-apps"
            target="_blank"
            rel="noopener noreferrer"
            onClick={ handleLearnMoreClick }
            style={{ color: '#0066FF', textDecoration: 'underline' }}
          >
            { t('onboarding:tracking_mode_step.variant_b.info.link_text') }
          </a> { t('onboarding:tracking_mode_step.variant_b.info.about_text') }
        </p>
      }
    />
  );
};

const InfoComponent: FC = () => {
  const { Dialog, showDialog } = useDialog();
  const { trackAppEvent } = useTracking();
  const { t } = useTranslation();
  const growthBook = useGrowthBook();
  const useFriendlyText = growthBook?.getFeatureValue<boolean>('silent-to-automatic', false);
  const dialogTitleKey = useFriendlyText
    ? 'onboarding:tracking_mode_step.automatic_vs_interactive'
    : 'onboarding:tracking_mode_step.silent_vs_interactive';

  const handleClick = () => {
    trackAppEvent('Clicked Learn More Onboarding', { category: 'Onboarding' });
    showDialog();
  };

  return (
    <MuiThemeProvider theme={ theme }>
      <Trans
        i18nKey="onboarding:tracking_mode_step.info"
        components={{
          b: <span className="color-primary" style={{ cursor: 'pointer' }} onClick={ handleClick } />,
        }}
      />
      <Dialog>
        {{
          title: <MuiTypography variant="h4" className="pr-sm8-4">{ t(dialogTitleKey) }</MuiTypography>,
          content: <TrackingSilentInteractiveContent />,
        }}
      </Dialog>
    </MuiThemeProvider>
  );
};

export const TrackingModeStepInfo: StepInfo<TrackingModeStep> = {
  Component: TrackingModeStepComponent,
  id: 'trackingModeStep',
  stepName: 'Tracking Type Screen',
  title: 'onboarding:tracking_mode_step.title',
  desc: 'onboarding:tracking_mode_step.description',
  Info: <InfoComponent />,
};
